import type { ReactElement } from 'react';
import type { AuditEvent } from '@contracts/orgs';
import { useOrg, useOrgAudit } from '../api/queries';
import { ApiError } from '../api/client';
import { Broken, Card, Empty, Pill, SkeletonRows } from '../ui';
import { since } from '../lib/time';
import { SettingsLayout } from '../settings/SettingsLayout';

/**
 * What has been shared, deleted, exported and changed here (checklist B7).
 *
 * The page exists so the privacy page can say "sharing and deletions are
 * logged" and be telling the truth — it said so for a day while no such table
 * existed, which is the kind of sentence that makes a privacy notice a
 * liability rather than a promise.
 *
 * **Acts, never content.** There is nothing on this page an administrator
 * could not have worked out from what they already administer; the words
 * anybody said are not here, and a workspace administrator still cannot open a
 * colleague's meeting.
 */
export function AuditLog(): ReactElement {
  const org = useOrg();
  const log = useOrgAudit();
  const forAdmins = log.isError && log.error instanceof ApiError && log.error.status === 404;

  return (
    <SettingsLayout
      title="What has happened here"
      sub="who shared, deleted, exported or changed something — acts, never what anybody said"
    >
      {log.isPending ? (
        <SkeletonRows rows={6} />
      ) : forAdmins ? (
        <Card flush>
          <Empty lead="This is for administrators.">
            {org.data
              ? `An administrator of ${org.data.name} can read it.`
              : 'An administrator of your workspace can read it.'}
          </Empty>
        </Card>
      ) : log.isError ? (
        <Card flush>
          <Broken what="The log" retry={() => void log.refetch()} />
        </Card>
      ) : log.data.length === 0 ? (
        <Card flush>
          <Empty lead="Nothing yet.">
            Sharing a meeting, deleting one, changing how long transcripts are kept or changing
            somebody’s role all appear here. Entries are kept for a year.
          </Empty>
        </Card>
      ) : (
        <Card
          title="Newest first"
          aside={<span className="t-secondary">kept for a year, then removed</span>}
          flush
        >
          {/* `log-table` is what the phone rules in components.css hang off. */}
          <table className="table log-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Who</th>
                <th>What</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {log.data.map((event, i) => (
                <tr key={`${event.at}-${i}`}>
                  <td className="mono" title={new Date(event.at).toLocaleString()}>
                    {since(event.at)}
                  </td>
                  <td>{event.actorEmail}</td>
                  <td>
                    <Pill tone={TONE[event.action] ?? undefined}>{words(event.action)}</Pill>
                  </td>
                  <td className="t-secondary">{detailOf(event)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </SettingsLayout>
  );
}

/** Amber for the acts that widen an audience or take something away for good. */
const TONE: Record<string, 'amber' | 'green' | undefined> = {
  'share.link.changed': 'amber',
  'meeting.deleted': 'amber',
  'account.deleted': 'amber',
  'workspace.deleted': 'amber',
  'transcript.removed': 'amber',
  'account.exported': 'amber',
  // A key that can read again and again, for as long as nobody notices it.
  'mcp.token.minted': 'amber',
  'import.started': 'amber',
};

const WORDS: Record<string, string> = {
  'share.granted': 'shared with a person',
  'share.revoked': 'took a person off',
  'share.space.added': 'added to a collection',
  'share.space.removed': 'took it out of a collection',
  'channel.created': 'made a collection',
  'channel.deleted': 'deleted a collection',
  'share.folder.filed': 'filed in a folder',
  'share.folder.removed': 'took it out of a folder',
  'share.link.changed': 'changed the link',
  'meeting.deleted': 'deleted a meeting',
  'account.deleted': 'deleted an account',
  'workspace.deleted': 'deleted the organisation',
  'transcript.removed': 'transcript removed by retention',
  'account.exported': 'downloaded their data',
  'workspace.settings.changed': 'changed a setting',
  'workspace.role.changed': 'changed a role',
  'person.searched': 'looked somebody up',
  'person.pseudonymised': 'removed somebody’s name',
  'mcp.token.minted': 'made an assistant key',
  'mcp.token.revoked': 'revoked an assistant key',
  'import.started': 'brought a history in',
  'import.cancelled': 'stopped bringing a history in',
};

function words(action: string): string {
  return WORDS[action] ?? action;
}

/** One line a person can read, built from whichever keys this act carries. */
function detailOf(event: AuditEvent): string {
  const d = event.detail;
  const parts: string[] = [];
  if (event.targetEmail) parts.push(event.targetEmail);
  if (typeof d.title === 'string') parts.push(`“${d.title}”`);
  if (typeof d.setting === 'string')
    parts.push(`${d.setting}: ${format(d.from)} → ${format(d.to)}`);
  else if (d.from !== undefined && d.to !== undefined)
    parts.push(`${format(d.from)} → ${format(d.to)}`);
  if (typeof d.role === 'string') parts.push(d.role.toLowerCase());
  if (typeof d.meetings === 'number')
    parts.push(`${d.meetings} meeting${d.meetings === 1 ? '' : 's'}`);
  if (d.by === 'retention' && typeof d.days === 'number') parts.push(`after ${d.days} days`);
  if (typeof d.from === 'string' && typeof d.source === 'string')
    parts.push(`${d.source.toLowerCase()} · ${d.from}`);
  if (typeof d.space === 'string') parts.push(`into ${d.space}`);
  if (typeof d.prefix === 'string') parts.push(d.prefix);
  if (typeof d.scopes === 'string' && d.scopes !== '') parts.push(d.scopes);
  // Not through `format`, which reads null as "kept": here it means the key
  // was minted with no expiry at all, which is the line worth reading.
  if (d.expiresAt !== undefined)
    parts.push(
      typeof d.expiresAt === 'string' ? `until ${d.expiresAt.slice(0, 10)}` : 'never expires',
    );
  return parts.join(' · ') || '—';
}

function format(value: unknown): string {
  if (value === null) return 'kept';
  if (value === true) return 'on';
  if (value === false) return 'off';
  return String(value);
}
